import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * MockLocationDetector — Capacitor plugin bridge for native anti-mock GPS detection.
 *
 * On Android: calls the native MockLocationDetectorPlugin (Java) which performs:
 *   - Mock setting / developer option checks
 *   - Installed fake GPS app scanning (25+ known packages + heuristic name matching)
 *   - Active test-provider detection
 *   - Location.isFromMockProvider() / Location.isMock() (API 31+)
 *   - Basic root detection
 *   - USB debugging check
 *
 * On Web: returns a safe-by-default stub (web has no mock location concept).
 */

const NativeMockDetector = Capacitor.isNativePlatform()
  ? registerPlugin('MockLocationDetector')
  : null;

/**
 * Run a comprehensive mock location detection scan.
 * Returns { isSafe, riskScore, indicators[], mockAppsFound, ... }
 */
export const detectMockLocation = async () => {
  if (!Capacitor.isNativePlatform() || !NativeMockDetector) {
    return getWebFallbackResult();
  }

  try {
    const result = await NativeMockDetector.detect();
    return {
      isSafe: result.isSafe,
      riskScore: result.riskScore || 0,
      mockSettingEnabled: result.mockSettingEnabled || false,
      mockAppsFound: result.mockAppsFound || 0,
      mockApps: result.mockApps || [],
      activeTestProvider: result.activeTestProvider || 'none',
      rootDetected: result.rootDetected || false,
      lastLocationMocked: result.lastLocationMocked || false,
      usbDebuggingEnabled: result.usbDebuggingEnabled || false,
      indicators: result.indicators || [],
      platform: result.platform || 'android',
      apiLevel: result.apiLevel || 0,
      source: 'native',
    };
  } catch (error) {
    console.warn('[MockDetector] Native detection failed:', error);
    return {
      ...getWebFallbackResult(),
      error: error.message,
      source: 'native_error',
    };
  }
};

/**
 * Quick boolean check — is the device safe from mock GPS?
 * Faster than full detect(), suitable for pre-flight checks.
 */
export const quickMockCheck = async () => {
  if (!Capacitor.isNativePlatform() || !NativeMockDetector) {
    return { isSafe: true, source: 'web' };
  }

  try {
    const result = await NativeMockDetector.quickCheck();
    return { isSafe: result.isSafe, source: 'native' };
  } catch (error) {
    console.warn('[MockDetector] Quick check failed:', error);
    return { isSafe: true, source: 'native_error', error: error.message };
  }
};

/**
 * Validate specific coordinates for mock-location indicators.
 */
export const validateCoordinates = async (latitude, longitude, accuracy) => {
  if (!Capacitor.isNativePlatform() || !NativeMockDetector) {
    return { flags: [], flagCount: 0, source: 'web' };
  }

  try {
    const result = await NativeMockDetector.validateLocation({ latitude, longitude, accuracy });
    return {
      flags: result.flags || [],
      flagCount: result.flagCount || 0,
      source: 'native',
    };
  } catch (error) {
    console.warn('[MockDetector] Coordinate validation failed:', error);
    return { flags: [], flagCount: 0, source: 'native_error' };
  }
};

/**
 * Web fallback — no mock detection available on web platform.
 */
function getWebFallbackResult() {
  return {
    isSafe: true,
    riskScore: 0,
    mockSettingEnabled: false,
    mockAppsFound: 0,
    mockApps: [],
    activeTestProvider: 'none',
    rootDetected: false,
    lastLocationMocked: false,
    usbDebuggingEnabled: false,
    indicators: [],
    platform: 'web',
    apiLevel: 0,
    source: 'web',
  };
}
